import PhotoSwipeLightbox from '@theme/photoswipe/lightbox';
import PhotoSwipe from '@theme/photoswipe';

export class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.thumbsSlider = null;
    this.pswpInstance = null;
    this.resizeObserver = null;
    this.lastWidth = 0;
    this.speed = 300;
    this.mediaCount = 0;
    this.direction = 'vertical';
  }

  connectedCallback() {
    // Query for elements after connected to DOM
    this.sliderContainer = this.querySelector('.swiper');
    this.thumbsContainer = this.querySelector('.swiper-thumbs');
    this.navigation = this.querySelector('.product-media-gallery__navigation');
    this.pagination = this.querySelector('.slideshow__controls');
    this.mediaCount = this.querySelectorAll('.swiper-slide').length;
    this.direction = this.dataset.direction || 'vertical';

    // Only initialize if we have carousel presentation and container
    if (this.sliderContainer) {
      // Initialize thumbs first, then main slider
      if (this.thumbsContainer) {
        this.initializeThumbs();
      }
      this.initializeSlider();
      // PhotoSwipe will be initialized after Swiper in initializeSlider
      this.setupResizeObserver();
    }
  }

  initializeThumbs() {
    const _this = this;
    if (!this.thumbsContainer) return;

    // Destroy existing thumbs slider if present
    if (this.thumbsSlider && typeof this.thumbsSlider.destroy === 'function') {
      this.thumbsSlider.destroy();
      this.thumbsSlider = null;
    }

    this.thumbsSlider = new Swiper(this.thumbsContainer, {
      spaceBetween: 0,
      slidesPerView: 4,
      direction: _this.direction,
      freeMode: true,
      watchSlidesProgress: true,
      breakpoints: {
        640: {
          slidesPerView: 5,
        },
        768: {
          slidesPerView: 6,
        },
        1024: {
          slidesPerView: 8,
        },
      },
    });
  }

  buildOptions() {
    const _this = this;
    const options = {
      loop: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      speed: this.speed,
      spaceBetween: 0,
      preloadImages: false,
      watchSlidesProgress: true,
      allowTouchMove: true,
      simulateTouch: true,
      grabCursor: true,
    };

    // Add thumbnails if available
    if (this.thumbsSlider) {
      options.thumbs = {
        swiper: this.thumbsSlider,
      };
    } else {
      console.warn('No thumbs slider available');
    }

    // Add navigation if arrows exist
    if (this.navigation) {
      options.navigation = {
        nextEl: this.navigation.querySelector('.swiper-button-next'),
        prevEl: this.navigation.querySelector('.swiper-button-prev'),
      };
    }

    // Add pagination if controls exist
    if (this.pagination) {
      const paginationType = this.pagination.dataset.style || 'bullets';
      
      if (paginationType === 'thumbnails') {
        // Custom thumbnails handled separately
        options.on = {
          slideChange: (swiper) => this.updateThumbnails(swiper.activeIndex),
        };
      } else if (paginationType === 'counter') {
        options.pagination = {
          el: '.slideshow__counter',
          type: 'custom',
          renderCustom: (swiper, current, total) => {
            return `${current} / ${total}`;
          },
        };
      } else {
        // dots/bullets
        options.pagination = {
          el: '.slideshow__pagination',
          type: 'bullets',
          clickable: true,
        };
      }
    }

    return options;
  }

  initializePhotoSwipe() {
    // Clean up existing instance
    if (this.pswpInstance) {
      this.pswpInstance.destroy();
      this.pswpInstance = null;
    }

    // Get all gallery items
    const galleryItems = this.querySelectorAll('.pswp-item');
    if (!galleryItems.length) {
      console.warn('No .pswp-item elements found');
      return;
    }

    console.log('Initializing PhotoSwipe with', galleryItems.length, 'items');

    // Initialize PhotoSwipe lightbox
    const container = this.querySelector('.swiper') || this;
    this.pswpInstance = new PhotoSwipeLightbox({
      gallery: container,
      children: '.pswp-item',
      pswpModule: PhotoSwipe,
      
      // UI options
      showHideAnimationType: 'none',
      bgOpacity: 0.9,
      spacing: 0.1,
      allowPanToNext: true,
      loop: true,
      
      // Swipe animation options
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      mainClass: 'pswp--animated',
      
      // Zoom options
      maxZoomLevel: 4,
      secondaryZoomLevel: 1.5,
      
      // Controls
      closeOnVerticalDrag: true,
      pinchToClose: true,
      escKey: true,
      arrowKeys: true,
      
      // Preload
      preload: [1, 2],
    });

    // Optional: Add custom UI elements or event listeners
    this.pswpInstance.on('uiRegister', function() {
      // Can add custom UI elements here if needed
    });

    this.pswpInstance.init();
  }

  initializeSlider() {
    if (!this.sliderContainer) return;
    
    // Destroy existing slider if present
    if (this.slider && typeof this.slider.destroy === 'function') {
      this.slider.destroy();
      this.slider = null;
    }

    const options = this.buildOptions();
    this.slider = new Swiper(this.sliderContainer, options);
      
    // Reinitialize PhotoSwipe after Swiper is ready
    // This ensures PhotoSwipe can properly access the slides
    setTimeout(() => {
      this.initializePhotoSwipe();
    }, 100);
  }

  setupThumbnailNavigation() {
    const thumbnails = this.querySelectorAll('.slideshow__thumbnail');
    if (!thumbnails.length || !this.slider) return;

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        if (this.slider) {
          this.slider.slideTo(index);
        }
      });
    });
  }

  updateThumbnails(activeIndex) {
    const thumbnails = this.querySelectorAll('.slideshow__thumbnail');
    if (!thumbnails.length) return;

    thumbnails.forEach((thumbnail, index) => {
      if (index === activeIndex) {
        thumbnail.classList.add('is-active');
        thumbnail.setAttribute('aria-selected', 'true');
      } else {
        thumbnail.classList.remove('is-active');
        thumbnail.setAttribute('aria-selected', 'false');
      }
    });
  }

  setupResizeObserver() {
    // Initialize with current width
    this.lastWidth = this.offsetWidth;

    // Create ResizeObserver to watch for width changes
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        
        // Only reinitialize if width actually changed
        if (Math.abs(newWidth - this.lastWidth) > 1) {
          this.lastWidth = newWidth;
          // Reinitialize thumbs first, then main slider
          if (this.thumbsContainer) {
            this.initializeThumbs();
          }
          this.initializeSlider();
        }
      }
    });

    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clean up Swiper instance
    if (this.slider && typeof this.slider.destroy === 'function') {
      this.slider.destroy();
      this.slider = null;
    }

    // Clean up thumbs Swiper instance
    if (this.thumbsSlider && typeof this.thumbsSlider.destroy === 'function') {
      this.thumbsSlider.destroy();
      this.thumbsSlider = null;
    }

    // Clean up PhotoSwipe instance
    if (this.pswpInstance) {
      this.pswpInstance.destroy();
      this.pswpInstance = null;
    }
  }
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}